import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { PhotoGalleryModal } from '../components/PhotoGalleryModal';
import { MapPin, ShieldCheck, Wifi, Zap, Droplets, Lock, Utensils, BedDouble, Maximize, Users, Star, Share2, Heart, ChevronRight } from 'lucide-react';
// Mock data - in a real app this would come from an API based on ID
const hostelData = {
  id: 1,
  name: 'Emerald Heights',
  location: 'Unilag, Akoka (5 min walk to gate)',
  price: '₦450,000',
  period: '/session',
  type: 'Self-contain',
  verified: true,
  rating: 4.8,
  reviews: 24,
  description: 'Experience premium student living at Emerald Heights. This fully serviced self-contain apartment offers the perfect balance of privacy and community. Recently renovated with modern fittings, 24/7 power supply via inverter backup, and high-speed internet access.',
  images: ['https://images.unsplash.com/photo-1555854877-bab0e564b8d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1484154218962-a1c002085d2f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80' // Exterior
  ],
  captions: ['Spacious Bedroom with Wardrobe', 'Modern Kitchen with Cabinets', 'En-suite Bathroom', 'Study Area', 'Kitchen Storage', 'Building Exterior'],
  amenities: {
    utilities: [{
      name: '24/7 Power',
      icon: Zap
    }, {
      name: 'Running Water',
      icon: Droplets
    }, {
      name: 'Free WiFi',
      icon: Wifi
    }],
    security: [{
      name: 'Gated Security',
      icon: Lock
    }, {
      name: 'CCTV Cameras',
      icon: ShieldCheck
    }],
    facilities: [{
      name: 'Kitchen',
      icon: Utensils
    }, {
      name: 'Study Room',
      icon: Maximize
    }]
  },
  specs: {
    size: '25 sqm',
    beds: '1 Double Bed',
    occupancy: 'Max 2 Students'
  }
};
export function HostelDetailPage() {
  const {
    id
  } = useParams();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const openGallery = (index: number) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };
  return <div className="min-h-screen bg-slate-50 pt-20" data-id="element-646" data-mp="bfwf49ez869t">
      <Navigation data-id="element-647" data-mp="bhfr3ntlnv5s" />

      <PhotoGalleryModal isOpen={isGalleryOpen} onClose={() => setIsGalleryOpen(false)} images={hostelData.images} captions={hostelData.captions} initialIndex={galleryIndex} data-id="element-648" data-mp="6gs5xis1y8jh" />

      {/* Hero Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-649" data-mp="3svl76u6rzerf">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-[400px] md:h-[500px]" data-id="element-650" data-mp="3nx9xcjghd497">
          {/* Main Image */}
          <div className="md:col-span-3 h-full relative rounded-2xl overflow-hidden cursor-pointer group" onClick={() => openGallery(0)} data-id="element-651" data-mp="3jun3ye45vb9w">
            <img src={hostelData.images[0]} alt="Main view" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-652" data-mp="39y1wzn8kzq3f" />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" data-id="element-653" data-mp="186x6iuidwvko" />
            <Button className="absolute bottom-4 right-4 bg-white/90 text-slate-900 hover:bg-white" onClick={e => {
            e.stopPropagation();
            openGallery(0);
          }} data-id="element-654" data-mp="5tiwmf1tj3bx">
              View All Photos
            </Button>
          </div>

          {/* Side Thumbnails */}
          <div className="hidden md:flex flex-col gap-4 h-full" data-id="element-655" data-mp="v7l7zvvkkrl">
            {hostelData.images.slice(1, 4).map((img, idx) => <div key={idx} className="flex-1 relative rounded-xl overflow-hidden cursor-pointer group" onClick={() => openGallery(idx + 1)} data-id="element-656" data-mp="3n14i4p2emvq6" data-mpi={idx}>
                <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-657" data-mp="3e7hph7thu8l7" />
                {idx === 2 && hostelData.images.length > 4 && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-lg" data-id="element-658" data-mp="2knrxv1d51yft">
                    +{hostelData.images.length - 4} more
                  </div>}
              </div>)}
          </div>
        </div>
      </div>

      <Section className="!py-8" data-id="element-659" data-mp="2pno5dal1o804">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-660" data-mp="2pmmryzgk39f8">
          {/* Main Content */}
          <div className="flex-1" data-id="element-661" data-mp="33m4a4vzcjofm">
            {/* Header Info */}
            <div className="mb-8" data-id="element-662" data-mp="2zlu11ag9chwj">
              <div className="flex flex-wrap gap-3 mb-4" data-id="element-663" data-mp="2zksqd735lxfc">
                <Badge variant="success" className="px-3 py-1 text-sm" data-id="element-664" data-mp="3dcphb7t361a5">
                  <ShieldCheck className="w-4 h-4 mr-1" data-id="element-665" data-mp="3df36j0134j9s" /> Verified Property
                </Badge>
                <Badge variant="neutral" className="px-3 py-1 text-sm" data-id="element-666" data-mp="38fyzaexq0vm3">
                  {hostelData.type}
                </Badge>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-id="element-667" data-mp="3id4rjqi2msnw">
                {hostelData.name}
              </h1>

              <div className="flex items-center text-slate-600 mb-4" data-id="element-668" data-mp="3ph35xr6qp8n2">
                <MapPin className="w-5 h-5 mr-2 text-amber-500" data-id="element-669" data-mp="3ufedfv9kb724" />
                {hostelData.location}
              </div>

              <div className="flex items-center gap-4 text-sm" data-id="element-670" data-mp="36qwdricu60d">
                <div className="flex items-center text-amber-500 font-bold" data-id="element-671" data-mp="7ztr42yvbli5">
                  <Star className="w-4 h-4 fill-current mr-1" data-id="element-672" data-mp="cy4xg3pyfdvm" />
                  {hostelData.rating} ({hostelData.reviews} reviews)
                </div>
                <button className="text-slate-500 hover:text-slate-900 flex items-center" data-id="element-673" data-mp="gxnakdbjaknw">
                  <Share2 className="w-4 h-4 mr-1" data-id="element-674" data-mp="lz2ug21z6l4p" /> Share
                </button>
                <button className="text-slate-500 hover:text-red-500 flex items-center" data-id="element-675" data-mp="qwbfcaeid39n">
                  <Heart className="w-4 h-4 mr-1" data-id="element-676" data-mp="2nqy46c4o8fka" /> Save
                </button>
              </div>
            </div>

            <div className="h-px bg-slate-200 mb-8" data-id="element-677" data-mp="2spj4ebjxaz8q" />

            {/* Description */}
            <div className="mb-10" data-id="element-678" data-mp="2l6jj20cwixmq">
              <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-679" data-mp="2l7kysi4v48as">
                About this place
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg" data-id="element-680" data-mp="2bfopsc18mxrs">
                {hostelData.description}
              </p>
            </div>

            {/* Specs */}
            <div className="grid grid-cols-3 gap-4 mb-10" data-id="element-681" data-mp="26ehfo6bmhmhy">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center" data-id="element-682" data-mp="343b0s5csp4oo">
                <Maximize className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-683" data-mp="2z6avhwnrs5ym" />
                <div className="font-bold text-slate-900" data-id="element-684" data-mp="2u5wmocecgrzj">
                  {hostelData.specs.size}
                </div>
                <div className="text-xs text-slate-500" data-id="element-685" data-mp="2p61v06mqvydf">Room Size</div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center" data-id="element-686" data-mp="1ir9lokjn9zv8">
                <BedDouble className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-687" data-mp="1dresubgb707j" />
                <div className="font-bold text-slate-900" data-id="element-688" data-mp="23g2506p0uwtr">
                  {hostelData.specs.beds}
                </div>
                <div className="text-xs text-slate-500" data-id="element-689" data-mp="28ff2dldirmal">Bed Type</div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center" data-id="element-690" data-mp="1ufvumtomecyt">
                <Users className="w-6 h-6 mx-auto mb-2 text-slate-400" data-id="element-691" data-mp="1ygpl9utg65ve" />
                <div className="font-bold text-slate-900" data-id="element-692" data-mp="1yishiwl70v8k">
                  {hostelData.specs.occupancy}
                </div>
                <div className="text-xs text-slate-500" data-id="element-693" data-mp="2r666o8ingmi4">Occupancy</div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-10" data-id="element-694" data-mp="2ddqbtuzwjusa">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-695" data-mp="2dcf5bnf2o8xh">
                What this place offers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12" data-id="element-696" data-mp="354w3dn1k4k5y">
                {Object.entries(hostelData.amenities).map(([category, items]) => items.map((item, idx) => <div key={`${category}-${idx}`} className="flex items-center gap-3 text-slate-700" data-id="element-697" data-mp="ygdqeq74919z" data-mpi={idx}>
                      <item.icon className="w-6 h-6 text-slate-400" data-id="element-698" data-mp="186p3b28s2kt0" />
                      <span className="text-lg" data-id="element-699" data-mp="1891i56j8of1u">{item.name}</span>
                    </div>))}
              </div>
            </div>

            {/* Reviews Preview */}
            <div className="mb-10" data-id="element-700" data-mp="fkds6xnjp1hy">
              <div className="flex justify-between items-center mb-6" data-id="element-701" data-mp="ajq34hivmuqd">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-702" data-mp="alkqs3lj90y5">Reviews</h2>
                <Link to="#" className="text-amber-600 font-medium hover:underline" data-id="element-703" data-mp="phs7gtxqfk8o">
                  View all {hostelData.reviews} reviews
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-704" data-mp="1vwt6j8i20ijh">
                {[1, 2].map(i => <div key={i} className="bg-slate-50 p-6 rounded-xl border border-slate-100" data-id="element-705" data-mp="2lut1eszcau4s">
                    <div className="flex items-center gap-3 mb-3" data-id="element-706" data-mp="2gvzwhoy4cbg8">
                      <div className="w-10 h-10 bg-slate-200 rounded-full" data-id="element-707" data-mp="2c3gbw810kg1x" />
                      <div data-id="element-708" data-mp="275wy1988a5ai">
                        <div className="font-bold text-slate-900" data-id="element-709" data-mp="274lunaip2zjx">
                          Student Name
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-710" data-mp="223z5tphuo05f">
                          October 2023
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm" data-id="element-711" data-mp="1t5vsvhkxkbp8">
                      "Great place to stay! The power supply is actually 24/7 as
                      advertised. Security is tight and the environment is
                      conducive for reading."
                    </p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:w-96 flex-shrink-0" data-id="element-712" data-mp="3q28mft36qlhm">
            <div className="sticky top-24" data-id="element-713" data-mp="3v24fixacgsie">
              <Card className="shadow-lg border-slate-200" data-id="element-714" data-mp="3v1360x8z8e2s">
                <CardContent className="p-6" data-id="element-715" data-mp="3pta08sjtv8s">
                  <div className="flex justify-between items-end mb-6" data-id="element-716" data-mp="3quoyocor1vs">
                    <div data-id="element-717" data-mp="8mtggktb900f">
                      <span className="text-3xl font-bold text-slate-900" data-id="element-718" data-mp="3758hp3dgfsqr">
                        {hostelData.price}
                      </span>
                      <span className="text-slate-500" data-id="element-719" data-mp="3c5d2h5leb9ms">
                        {hostelData.period}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6" data-id="element-720" data-mp="3m30ydyuqmn6j">
                    <div className="p-3 border border-slate-200 rounded-lg" data-id="element-721" data-mp="3m0eo3rggptkm">
                      <div className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-722" data-mp="wcnsj05t2kjm">
                        Move-in Date
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-723" data-mp="2xf560ybyq3v6">
                        Select Date
                      </div>
                    </div>
                    <div className="p-3 border border-slate-200 rounded-lg" data-id="element-724" data-mp="2sg29y3dyn7zc">
                      <div className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-725" data-mp="36es7fk4oz9o8">
                        Duration
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-726" data-mp="2eox60p3k7tj7">
                        1 Session (9 Months)
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mb-3" size="lg" data-id="element-727" data-mp="29nzpegy777jt">
                    Reserve Now
                  </Button>
                  <Button variant="outline" className="w-full mb-6" data-id="element-728" data-mp="2iqr3ypmdejfo">
                    Schedule Tour
                  </Button>

                  <div className="space-y-3 text-sm text-slate-500" data-id="element-729" data-mp="1uzv4r7fl4lye">
                    <div className="flex justify-between" data-id="element-730" data-mp="1qzkxzsjyxrih">
                      <span data-id="element-731" data-mp="1qz2vie6pqh24">Rent</span>
                      <span data-id="element-732" data-mp="1gvo20lfp98vc">{hostelData.price}</span>
                    </div>
                    <div className="flex justify-between" data-id="element-733" data-mp="1gtjodbs00vhv">
                      <span data-id="element-734" data-mp="1lmwclksrn0a3">Service Charge</span>
                      <span data-id="element-735" data-mp="16x8dmq824dpj">₦50,000</span>
                    </div>
                    <div className="flex justify-between" data-id="element-736" data-mp="16z1ko6ml0zy2">
                      <span data-id="element-737" data-mp="1bvi971xp1gbo">Caution Fee</span>
                      <span data-id="element-738" data-mp="1bu73uxolo0ig">₦30,000</span>
                    </div>
                    <div className="h-px bg-slate-200 my-2" data-id="element-739" data-mp="1zmxbiupgzxmx" />
                    <div className="flex justify-between font-bold text-slate-900 text-base" data-id="element-740" data-mp="24kp5rvdl7vua">
                      <span data-id="element-741" data-mp="1pnpyk8mne50c">Total</span>
                      <span data-id="element-742" data-mp="1pmogvlb4mk9n">₦530,000</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-6 bg-amber-50 p-4 rounded-xl border border-amber-100 flex gap-3" data-id="element-743" data-mp="1uom4ssuh238x">
                <ShieldCheck className="w-6 h-6 text-amber-600 flex-shrink-0" data-id="element-744" data-mp="eavzjirm98xp" />
                <div data-id="element-745" data-mp="j8xgny5hey6j">
                  <div className="font-bold text-amber-800 text-sm" data-id="element-746" data-mp="j59op3foqjtx">
                    Simba Guarantee
                  </div>
                  <div className="text-xs text-amber-700 mt-1" data-id="element-747" data-mp="unilo6dl7jdl">
                    Your payment is held in escrow until you verify the property
                    in person.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>;
}